import { z } from 'zod';
import { isoTimestampSchema } from './common';

/*
 * Account lifecycle (ADR-0031): invitations, password reset, e-mail verification and TOTP.
 * Public endpoints take a one-time token from an e-mail link; only its SHA-256 is stored.
 */

const passwordSchema = z.string().min(10).max(256);
/** One-time tokens are 32 random bytes, base64url (43 characters). */
export const accountTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{20,128}$/);

/** `GET /v1/account/invitations/:token` (public). */
export const invitationInfoSchema = z.object({
  tenant_name: z.string(),
  email: z.string(),
  display_name: z.string(),
  expires_at: isoTimestampSchema,
  /** Terms of service version the person accepts by joining. */
  terms_version: z.string().nullable(),
  terms_url: z.string().nullable(),
});
export type InvitationInfo = z.infer<typeof invitationInfoSchema>;

export const acceptInvitationRequestSchema = z.object({
  password: passwordSchema,
  display_name: z.string().trim().min(1).max(120).optional(),
  accept_terms: z.literal(true),
});
export type AcceptInvitationRequest = z.input<typeof acceptInvitationRequestSchema>;

export const passwordResetRequestSchema = z.object({
  email: z
    .email()
    .max(254)
    .transform((e) => e.toLowerCase()),
});

export const passwordResetConfirmSchema = z.object({ password: passwordSchema });

/** `POST /v1/account/password-reset` always answers 202 (no account enumeration). */
export const acceptedResponseSchema = z.object({ status: z.literal('accepted') });

export const emailVerificationResponseSchema = z.object({
  status: z.enum(['sent', 'already_verified', 'verified']),
});

/** `GET /v1/account` — the signed-in user's security settings. */
export const accountSecuritySchema = z.object({
  email: z.string(),
  email_verified_at: isoTimestampSchema.nullable(),
  mfa_enabled: z.boolean(),
  /** TOTP is offered to owners, administrators and managers (and platform operators). */
  mfa_eligible: z.boolean(),
  terms_accepted_at: isoTimestampSchema.nullable(),
  terms_version: z.string().nullable(),
});
export type AccountSecurity = z.infer<typeof accountSecuritySchema>;

/**
 * `POST /v1/account/mfa/totp/setup`: a fresh secret, not stored yet. The client shows it
 * (QR / manual key) and sends it back with the first code to `…/confirm`.
 */
export const totpSetupResponseSchema = z.object({
  secret: z.string(),
  otpauth_uri: z.string(),
});
export type TotpSetup = z.infer<typeof totpSetupResponseSchema>;

const totpCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/);

export const totpConfirmRequestSchema = z.object({
  secret: z.string().regex(/^[A-Z2-7]{32}$/),
  code: totpCodeSchema,
});

export const totpDisableRequestSchema = z.object({ code: totpCodeSchema });
